/**
 * Когда входящий звонок перестаёт быть входящим для нас.
 *
 * <p><b>Дефект, ради которого появился модуль.</b> Экран входящего (оверлей на Android и
 * в вебе, CallKit на iOS) показывался по кадру `invite` — и не снимался <b>ничем</b>.
 * Всё, что могло его отменить, разбирал только {@code useVoiceCallSession}, то есть экран
 * звонка, которого во время звонка ещё нет: человек сидит в списке чатов, звонок звонит
 * из оверлея. В итоге:
 *
 * <ul>
 *   <li>вошли в аккаунт с двух устройств, ответили с компьютера — <b>телефон продолжал
 *       звонить</b>, пока не истечёт таймаут. Это и есть жалоба, с которой всё началось:
 *       `accept` от собственного второго устройства приезжал на телефон и не значил там
 *       ничего;</li>
 *   <li>звонящий передумал и положил трубку — у принимающего звонок звонил дальше;</li>
 *   <li>ответили с телефона, а планшет звонил ещё полминуты.</li>
 * </ul>
 *
 * <p>Правила здесь чистые: на вход — что у нас звонит и какой кадр пришёл, на выход —
 * снимать или оставить. Ни таймеров, ни сети, ни React — чтобы всю решётку случаев
 * («своё устройство / чужое», «прямой / групповой», «та нога / чужая») можно было
 * перебрать тестами, а не двумя телефонами и планшетом.
 *
 * <p><b>Групповой звонок — особый случай, и не по недосмотру.</b> Там `accept` и `decline`
 * <i>другого</i> приглашённого нас не касаются: он ответил, а мы всё ещё приглашены и
 * имеем полное право войти позже. Снимать по ним экран значило бы отбирать звонок у
 * всех, кроме самого быстрого.
 */

import { normCallUserId } from './callSignalRules';
import { normCallChatId } from './callBusyRules';

/** Входящий звонок, который прямо сейчас звонит у нас. */
export type RingingIncomingCall = {
  chatId: string;
  /** Нога звонка (`callInviteId`), если её успели узнать. */
  inviteId: string | null;
  scope: 'direct' | 'group';
  /** Кто звонит. Пусто, пока неизвестно (старый пуш, холодный старт). */
  callerUserId: string | null;
};

/** Разобранный сигнальный кадр — ровно те поля, от которых зависит решение. */
export type IncomingRingSignal = {
  chatId: string;
  signalType: string;
  fromUserId: string | null;
  /** `callInviteId` из кадра, если он там есть. */
  legFromMessage: string | null;
  /** Наш собственный id — им отличают своё второе устройство от чужого человека. */
  selfUserId: string | null;
};

export type RingCancelReason =
  /** Ответили с другого нашего устройства. */
  | 'answered_elsewhere'
  /** Отклонили с другого нашего устройства. */
  | 'declined_elsewhere'
  /** Звонящий положил трубку, не дождавшись ответа. */
  | 'caller_hung_up'
  /** Разговор закончили с другого нашего устройства. */
  | 'ended_elsewhere';

export type RingKeepReason =
  | 'other_chat'
  | 'foreign_leg'
  | 'not_a_cancel_signal'
  /** В группе ответил/отказался другой приглашённый — мы всё ещё приглашены. */
  | 'group_other_member';

export type RingCancelDecision =
  | { action: 'cancel'; reason: RingCancelReason }
  | { action: 'keep'; reason: RingKeepReason };

/**
 * Снимать ли экран входящего звонка по этому кадру.
 *
 * <p>Порядок проверок важен так же, как в {@link import('./callSignalRules')}: сначала
 * «наш ли это вообще разговор» (чат, затем нога), и только потом — что именно случилось.
 * Кадр из другого чата или от другой ноги не должен влиять на наш звонок ни при каких
 * обстоятельствах: в чате может идти второй вызов, а звонков в разных чатах — сколько
 * угодно.
 */
export function decideIncomingRingCancel(
  ringing: RingingIncomingCall,
  signal: IncomingRingSignal,
): RingCancelDecision {
  if (normCallChatId(ringing.chatId) !== normCallChatId(signal.chatId)) {
    return { action: 'keep', reason: 'other_chat' };
  }
  // Нога сверяется, только если известна обеим сторонам: старые сборки её не шлют, и
  // терять из-за этого отмену нельзя — зависший экран входящего не лечится ничем, кроме
  // перезапуска приложения.
  if (ringing.inviteId && signal.legFromMessage && ringing.inviteId !== signal.legFromMessage) {
    return { action: 'keep', reason: 'foreign_leg' };
  }

  const from = normCallUserId(signal.fromUserId);
  const self = normCallUserId(signal.selfUserId);
  const fromIsSelf = from !== '' && self !== '' && from === self;
  const caller = normCallUserId(ringing.callerUserId);
  const fromIsCaller = from !== '' && caller !== '' && from === caller;

  switch (signal.signalType) {
    case 'accept':
      // Единственный случай, когда `accept` снимает наш звонок: ответили мы сами, просто
      // с другого устройства. Ответ любого другого участника группы нас не касается.
      return fromIsSelf
        ? { action: 'cancel', reason: 'answered_elsewhere' }
        : { action: 'keep', reason: 'group_other_member' };
    case 'decline':
      return fromIsSelf
        ? { action: 'cancel', reason: 'declined_elsewhere' }
        : { action: 'keep', reason: 'group_other_member' };
    case 'bye': {
      if (fromIsSelf) {
        // Наше же второе устройство ответило и положило трубку — звонка больше нет.
        return { action: 'cancel', reason: 'ended_elsewhere' };
      }
      if (fromIsCaller) {
        return { action: 'cancel', reason: 'caller_hung_up' };
      }
      if (ringing.scope === 'direct') {
        // В прямом звонке сторон две. Даже если отправитель неизвестен, `bye` здесь может
        // быть только от звонящего — больше некому.
        return { action: 'cancel', reason: 'caller_hung_up' };
      }
      // В группе `bye` — это «я вышел», а не «звонок окончен»: остальным всё ещё звонят.
      return { action: 'keep', reason: 'group_other_member' };
    }
    default:
      return { action: 'keep', reason: 'not_a_cancel_signal' };
  }
}
